/**
 * Site Icon Atlas for DeckGL IconLayer
 *
 * This module provides icon atlas configuration for rendering site markers
 * using DeckGL's GPU-accelerated IconLayer instead of DOM-based Google Maps markers.
 *
 * The icon atlas is a single image containing all icon variants, which DeckGL
 * can efficiently render using WebGL texture sampling.
 */

import { withDefaultSiteRing } from './defaultSiteMarkerStyle';
import {
  DROPOFF_PHASE_COLOR,
  DROPOFF_PHASE_COLOR_ACTIVE,
  PICKUP_PHASE_COLOR,
  PICKUP_PHASE_COLOR_ACTIVE,
} from './loadPhase';

/**
 * Icon dimensions in pixels.
 * These values match the existing site icons (24x24 display size with 32x32 viewBox).
 */
const ICON_SIZE = 32;

/**
 * Color definitions for site icon states.
 * These colors match the existing SiteIcons.tsx implementation. Pickup and
 * dropoff share their color with the truck pins headed toward them, so a truck
 * and its destination site read as the same phase color (see loadPhase.ts).
 */
const SITE_ICON_COLORS = {
  // Pickup site colors
  pickup: PICKUP_PHASE_COLOR, // Blue - pickup site
  pickupActive: PICKUP_PHASE_COLOR_ACTIVE, // Brighter blue - active/hovered pickup
  // Dropoff site colors
  dropoff: DROPOFF_PHASE_COLOR, // Dark navy - dropoff site
  dropoffActive: DROPOFF_PHASE_COLOR_ACTIVE, // Brighter navy - active/hovered dropoff
  // Both (pickup + dropoff) site colors
  both: '#1B2480', // Purple - both site type
  bothActive: '#3341CC', // Brighter purple - active/hovered both
  // Staging site colors
  staging: '#0891B2', // Teal/cyan - staging site
  stagingActive: '#0ABED9', // Brighter teal - active/hovered staging
  // Common colors
  stroke: '#FFFFFF', // White - icon stroke/outline
  arrow: '#FFFFFF', // White - arrow fill
} as const;

/**
 * SVG path for the up arrow (pickup site), normalized to 32x32 viewBox.
 * Scaled from original 36x36 viewBox path.
 */
const PICKUP_ARROW_PATH =
  'M9 16L10.26 17.26L15 12.52V23H17V12.52L21.73 17.27L23 16L16 9L9 16Z';

/**
 * SVG path for the down arrow (dropoff site).
 * Matches the path from SiteIcons.tsx DropoffSiteIcon
 */
const DROPOFF_ARROW_PATH =
  'M24 16L22.59 14.59L17 20.17V8H15V20.17L9.42 14.58L8 16L16 24L24 16Z';

/**
 * SVG path for the bi-directional arrows (both site type).
 * Matches the path from SiteIcons.tsx BothSiteIcon
 */
const BOTH_ARROWS_PATH =
  'M20 21.01V14H18V21.01H15L19 25L23 21.01H20ZM13 7L9 10.99H12V18H14V10.99H17L13 7Z';

/**
 * SVG path for the right-pointing arrow (staging site).
 * Matches the path from TreadMarker.tsx stagingSiteSvg
 */
const STAGING_ARROW_PATH =
  'M16 8L14.59 9.41L20.17 15H8V17H20.17L14.58 22.58L16 24L24 16L16 8Z';

/**
 * Creates an SVG string for the pickup site icon (up arrow in circle).
 * All icons use 32x32 viewBox for consistent atlas rendering.
 *
 * @param fillColor - The fill color for the circle background
 * @returns SVG string that can be used in a data URL
 */
const createPickupSiteSvg = (fillColor: string): string => {
  // All icons use 32x32 viewBox for consistent atlas rendering
  // Circle radius is 15 (not 16) to account for 2px stroke on each side: 15 + 2px stroke = 17px, leaving 1px padding
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="15" fill="${fillColor}" stroke="${SITE_ICON_COLORS.stroke}" stroke-width="2"/>
    <path d="${PICKUP_ARROW_PATH}" fill="${SITE_ICON_COLORS.arrow}"/>
  </svg>`;
};

/**
 * Creates an SVG string for the dropoff site icon (down arrow in circle).
 *
 * @param fillColor - The fill color for the circle background
 * @returns SVG string that can be used in a data URL
 */
const createDropoffSiteSvg = (fillColor: string): string => {
  // Dropoff icon uses 32x32 viewBox
  // Circle radius is 15 (not 16) to account for 2px stroke on each side: 15 + 2px stroke = 17px, leaving 1px padding
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="15" fill="${fillColor}" stroke="${SITE_ICON_COLORS.stroke}" stroke-width="2"/>
    <path d="${DROPOFF_ARROW_PATH}" fill="${SITE_ICON_COLORS.arrow}"/>
  </svg>`;
};

/**
 * Creates an SVG string for the both-type site icon (bi-directional arrows in circle).
 *
 * @param fillColor - The fill color for the circle background
 * @returns SVG string that can be used in a data URL
 */
const createBothSiteSvg = (fillColor: string): string => {
  // Both icon uses 32x32 viewBox
  // Circle radius is 15 (not 16) to account for 2px stroke on each side: 15 + 2px stroke = 17px, leaving 1px padding
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="15" fill="${fillColor}" stroke="${SITE_ICON_COLORS.stroke}" stroke-width="2"/>
    <path d="${BOTH_ARROWS_PATH}" fill="${SITE_ICON_COLORS.arrow}"/>
  </svg>`;
};

/**
 * Creates an SVG string for the staging site icon (right-pointing arrow in circle).
 *
 * @param fillColor - The fill color for the circle background
 * @returns SVG string that can be used in a data URL
 */
const createStagingSiteSvg = (fillColor: string): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="15" fill="${fillColor}" stroke="${SITE_ICON_COLORS.stroke}" stroke-width="2"/>
    <path d="${STAGING_ARROW_PATH}" fill="${SITE_ICON_COLORS.arrow}"/>
  </svg>`;
};

/**
 * Icon types available in the site icon atlas.
 */
export type SiteIconType =
  | 'pickup_site'
  | 'pickup_site_active'
  | 'dropoff_site'
  | 'dropoff_site_active'
  | 'both_site'
  | 'both_site_active'
  | 'staging_site'
  | 'staging_site_active'
  // Default (candidate) site variants: muted icon + same-color dashed ring.
  | 'pickup_site_default'
  | 'dropoff_site_default'
  | 'both_site_default'
  | 'staging_site_default';

/**
 * Icon mapping configuration for DeckGL IconLayer.
 * Each entry defines the position and dimensions of an icon within the atlas.
 *
 * @property x - X position of the icon in the atlas (pixels)
 * @property y - Y position of the icon in the atlas (pixels)
 * @property width - Width of the icon (pixels)
 * @property height - Height of the icon (pixels)
 * @property anchorX - X anchor point for positioning (pixels from left)
 * @property anchorY - Y anchor point for positioning (pixels from top)
 * @property mask - If true, the icon color can be overridden via getColor
 */
interface IconMappingEntry {
  x: number;
  y: number;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  mask: boolean;
}

/**
 * Icon mapping for the site icon atlas.
 * Maps icon names to their positions and properties in the atlas image.
 * Icons are arranged in a 2-row grid: [pickup, dropoff, both] x [normal, active]
 */
export const SITE_ICON_MAPPING: Record<SiteIconType, IconMappingEntry> = {
  pickup_site: {
    x: 0,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  pickup_site_active: {
    x: ICON_SIZE,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  dropoff_site: {
    x: ICON_SIZE * 2,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  dropoff_site_active: {
    x: ICON_SIZE * 3,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  both_site: {
    x: ICON_SIZE * 4,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  both_site_active: {
    x: ICON_SIZE * 5,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  staging_site: {
    x: ICON_SIZE * 6,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  staging_site_active: {
    x: ICON_SIZE * 7,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  pickup_site_default: {
    x: ICON_SIZE * 8,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  dropoff_site_default: {
    x: ICON_SIZE * 9,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  both_site_default: {
    x: ICON_SIZE * 10,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
  staging_site_default: {
    x: ICON_SIZE * 11,
    y: 0,
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  },
};

/**
 * Total width of the icon atlas: 8 base variants + 4 default-site variants.
 */
const ATLAS_WIDTH = ICON_SIZE * 12;

/**
 * Total height of the icon atlas (single row of icons).
 */
const ATLAS_HEIGHT = ICON_SIZE;

/**
 * Creates the combined SVG atlas containing all site icon variants.
 * Icons are arranged horizontally:
 * [pickup, pickup_active, dropoff, dropoff_active, both, both_active, staging, staging_active]
 *
 * @returns SVG string containing all icons
 */
const createIconAtlasSvg = (): string => {
  const pickupSvg = createPickupSiteSvg(SITE_ICON_COLORS.pickup);
  const pickupActiveSvg = createPickupSiteSvg(SITE_ICON_COLORS.pickupActive);
  const dropoffSvg = createDropoffSiteSvg(SITE_ICON_COLORS.dropoff);
  const dropoffActiveSvg = createDropoffSiteSvg(SITE_ICON_COLORS.dropoffActive);
  const bothSvg = createBothSiteSvg(SITE_ICON_COLORS.both);
  const bothActiveSvg = createBothSiteSvg(SITE_ICON_COLORS.bothActive);
  const stagingSvg = createStagingSiteSvg(SITE_ICON_COLORS.staging);
  const stagingActiveSvg = createStagingSiteSvg(SITE_ICON_COLORS.stagingActive);

  // Default (candidate) site variants: muted icon + same-color dashed ring.
  const pickupDefaultSvg = withDefaultSiteRing(pickupSvg, SITE_ICON_COLORS.pickup);
  const dropoffDefaultSvg = withDefaultSiteRing(dropoffSvg, SITE_ICON_COLORS.dropoff);
  const bothDefaultSvg = withDefaultSiteRing(bothSvg, SITE_ICON_COLORS.both);
  const stagingDefaultSvg = withDefaultSiteRing(stagingSvg, SITE_ICON_COLORS.staging);

  // Extract inner content from each SVG (remove outer svg tags)
  const extractSvgContent = (svg: string): string => {
    const match = svg.match(/<svg[^>]*>([\s\S]*)<\/svg>/);
    return match ? match[1] : '';
  };

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ATLAS_WIDTH}" height="${ATLAS_HEIGHT}" viewBox="0 0 ${ATLAS_WIDTH} ${ATLAS_HEIGHT}">
    <g transform="translate(0, 0)">${extractSvgContent(pickupSvg)}</g>
    <g transform="translate(${ICON_SIZE}, 0)">${extractSvgContent(pickupActiveSvg)}</g>
    <g transform="translate(${ICON_SIZE * 2}, 0)">${extractSvgContent(dropoffSvg)}</g>
    <g transform="translate(${ICON_SIZE * 3}, 0)">${extractSvgContent(dropoffActiveSvg)}</g>
    <g transform="translate(${ICON_SIZE * 4}, 0)">${extractSvgContent(bothSvg)}</g>
    <g transform="translate(${ICON_SIZE * 5}, 0)">${extractSvgContent(bothActiveSvg)}</g>
    <g transform="translate(${ICON_SIZE * 6}, 0)">${extractSvgContent(stagingSvg)}</g>
    <g transform="translate(${ICON_SIZE * 7}, 0)">${extractSvgContent(stagingActiveSvg)}</g>
    <g transform="translate(${ICON_SIZE * 8}, 0)">${extractSvgContent(pickupDefaultSvg)}</g>
    <g transform="translate(${ICON_SIZE * 9}, 0)">${extractSvgContent(dropoffDefaultSvg)}</g>
    <g transform="translate(${ICON_SIZE * 10}, 0)">${extractSvgContent(bothDefaultSvg)}</g>
    <g transform="translate(${ICON_SIZE * 11}, 0)">${extractSvgContent(stagingDefaultSvg)}</g>
  </svg>`;
};

/**
 * The site icon atlas as a data URL.
 * This URL can be passed directly to DeckGL's IconLayer iconAtlas prop.
 *
 * Using a data URL allows the atlas to be embedded directly without
 * requiring a separate HTTP request to load an image file.
 */
export const SITE_ICON_ATLAS: string =
  'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(createIconAtlasSvg());

/**
 * Default icon size for rendering site markers.
 * This can be adjusted based on zoom level or user preferences.
 */
export const DEFAULT_SITE_ICON_SIZE = 24;

/**
 * Icon size when a site is in active/selected state.
 * Slightly larger to provide visual feedback.
 */
export const ACTIVE_SITE_ICON_SIZE = 28;
